// Users tab: lists Keycloak users with their assigned brand-mint client roles,
// a create-user form, and a per-user role assignment editor. The API enforces
// service-admin; this UI is a convenience layer over it.
import { useCallback, useEffect, useState } from "react";
import {
  ApiError,
  assignRoles,
  createUser,
  listRoles,
  listUsers,
  type AdminUser,
} from "../api";
import { Banner } from "./Banner";

export function UsersTab() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [u, r] = await Promise.all([listUsers(), listRoles()]);
      setUsers(u);
      setRoles(r);
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <section aria-labelledby="users-heading">
      <h2 id="users-heading" className="sr-only">
        Users
      </h2>

      {error && <Banner kind="error">{error}</Banner>}
      {ok && <Banner kind="ok">{ok}</Banner>}

      <CreateUserForm
        onCreated={(msg) => {
          setOk(msg);
          setError(null);
          void refresh();
        }}
        onError={(msg) => {
          setError(msg);
          setOk(null);
        }}
      />

      <div className="card">
        <h2>Users</h2>
        {loading ? (
          <p className="muted">Loading…</p>
        ) : users.length === 0 ? (
          <p className="muted">No users yet.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Username</th>
                <th>Email</th>
                <th>Enabled</th>
                <th>Roles</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <UserRow
                  key={u.id}
                  user={u}
                  allRoles={roles}
                  onAssigned={(msg) => {
                    setOk(msg);
                    setError(null);
                    void refresh();
                  }}
                  onError={(msg) => {
                    setError(msg);
                    setOk(null);
                  }}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function CreateUserForm(props: {
  onCreated: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await createUser({ username, email, password });
      props.onCreated(`Created user "${username}" (${res.id}).`);
      setUsername("");
      setEmail("");
      setPassword("");
    } catch (err) {
      props.onError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <h2>Create user</h2>
      <form className="stack" onSubmit={submit}>
        <label>
          Username
          <input
            type="text"
            required
            autoComplete="off"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </label>
        <label>
          Email
          <input
            type="email"
            required
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label>
          Password
          <input
            type="password"
            required
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        <div>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={busy || !username || !email || !password}
          >
            {busy ? "Creating…" : "Create user"}
          </button>
        </div>
      </form>
    </div>
  );
}

function UserRow(props: {
  user: AdminUser;
  allRoles: string[];
  onAssigned: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const { user, allRoles } = props;
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set(user.roles));
  const [busy, setBusy] = useState(false);

  // Keep the checkbox state in sync if the user's roles change after a refresh.
  useEffect(() => {
    setSelected(new Set(user.roles));
  }, [user.roles]);

  const toggle = (role: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(role)) next.delete(role);
      else next.add(role);
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      await assignRoles(user.id, [...selected]);
      props.onAssigned(`Updated roles for "${user.username}".`);
      setEditing(false);
    } catch (err) {
      props.onError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr>
      <td>{user.username}</td>
      <td>{user.email || <span className="muted">—</span>}</td>
      <td>
        <span className={user.enabled ? "enabled-yes" : "enabled-no"}>
          {user.enabled ? "yes" : "no"}
        </span>
      </td>
      <td>
        {editing ? (
          <fieldset
            className="checks"
            style={{ border: "none", padding: 0, margin: 0 }}
          >
            <legend className="sr-only">Roles for {user.username}</legend>
            {allRoles.map((role) => (
              <label key={role}>
                <input
                  type="checkbox"
                  checked={selected.has(role)}
                  onChange={() => toggle(role)}
                />
                {role}
              </label>
            ))}
          </fieldset>
        ) : user.roles.length === 0 ? (
          <span className="muted">none</span>
        ) : (
          user.roles.map((r) => (
            <span key={r} className="role-tag">
              {r}
            </span>
          ))
        )}
      </td>
      <td>
        {editing ? (
          <>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void save()}
              disabled={busy}
            >
              {busy ? "Saving…" : "Save"}
            </button>{" "}
            <button
              type="button"
              className="btn"
              onClick={() => {
                setSelected(new Set(user.roles));
                setEditing(false);
              }}
              disabled={busy}
            >
              Cancel
            </button>
          </>
        ) : (
          <button
            type="button"
            className="btn"
            onClick={() => setEditing(true)}
          >
            Assign roles
          </button>
        )}
      </td>
    </tr>
  );
}

function messageOf(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return "Unexpected error.";
}
